import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'

import authApi from '@/api/auth.api'
import WebviewService from '@/service/webview.service'
import { CryptoUtils } from '@/utils/cryptoUtils'

export function getScienceflatBaseUrl() {
  const { hostname } = window.location
  const scienceflatHostname = hostname.replace(/mathflat\.(com|co\.kr)$/, 'scienceflat.com')

  // local/preview 환경: localStorage.setItem('testScienceflatUrl', 'http://localhost:PORT') 로 설정
  if (scienceflatHostname === hostname) {
    return localStorage.getItem('testScienceflatUrl') ?? 'https://teacher.scienceflat.com'
  }

  return `https://${scienceflatHostname}`
}

/**
 * 매쓰플랫 JWT 토큰을 암호화해서 사이언스플랫 URL에 실어 이동 (SSO 역할)
 *
 * @param preOpenedPopup - await 전에 미리 열어둔 창. 전달하면 그 창의 URL을 교체하고,
 *   없으면 이 함수 안에서 직접 열거나(openInNewTab=true), 같은 탭으로 이동(openInNewTab=false)
 */
async function redirectWithToken(
  targetUrl: string,
  openInNewTab: boolean,
  preOpenedPopup?: Window | null,
) {
  // await 이후 window.open()은 브라우저 팝업 차단 대상이므로, gesture context 안에서 빈 창을 미리 열고 준비 완료 후 location만 교체한다.
  const popup =
    preOpenedPopup !== undefined
      ? preOpenedPopup
      : openInNewTab
        ? window.open('', '_blank')
        : null
  // opener 역참조 차단: 열린 창에서 window.opener로 부모 창에 접근하지 못하도록 보안 처리
  if (popup) popup.opener = null

  // 만료 직전 토큰 전송 방지를 위해 최신 토큰으로 재발급
  const { accessToken, refreshToken } = await jwtTokenService.reissueTokens()
  // URL에 토큰을 평문으로 실으면 브라우저 히스토리·네트워크 로그에 노출되므로 AES 암호화
  const { encrypted, key, iv } = await CryptoUtils.encryptAES(
    JSON.stringify({ accessToken, refreshToken }),
  )

  // 사이언스플랫 콜백 URL에 암호화 데이터를 query string으로 주입
  // 사이언스플랫은 encrypted+key+iv를 받아 복호화 후 로그인 처리
  const url = new URL(targetUrl)
  url.searchParams.set('encrypted', encrypted)
  url.searchParams.set('key', key)
  url.searchParams.set('iv', iv)

  if (popup) {
    // window.open(url)은 차단되지만 이미 열린 창의 location 교체는 차단 대상이 아님
    popup.location.replace(url.toString())
  } else {
    window.location.href = url.toString()
  }
}

function assertScienceflatRedirectUri(redirectUri: string) {
  const given = new URL(redirectUri)
  const allowed = new URL(`${getScienceflatBaseUrl()}/mathflat/callback`)
  if (given.origin !== allowed.origin || given.pathname !== allowed.pathname) {
    throw new Error('Invalid scienceflat redirectUri')
  }
}

/** 이미 동의된 사용자 — 토큰만 보내서 scienceflat 이동 (다른 계정 로그인 시 scienceflat 로그인 페이지로 이동됨) */
export async function openScienceflat() {
  await redirectWithToken(
    `${getScienceflatBaseUrl()}/mathflat/callback`,
    !WebviewService.isAppAndroid,
  )
}

/** 동의 API 호출 후 토큰 보내서 scienceflat 이동 */
export async function connectToScienceflat(redirectUri: string, openInNewTab = false) {
  assertScienceflatRedirectUri(redirectUri)
  // postAllowServiceType await 전(user gesture context 안)에 미리 빈 창을 열어둠
  const popup = openInNewTab ? window.open('', '_blank') : null
  if (popup) popup.opener = null
  await authApi.postAllowServiceType({ serviceType: 'SCIENCEFLAT' })
  await redirectWithToken(redirectUri, openInNewTab, popup)
}
